import { useState, useEffect } from 'react';
import { useCheckoutStore } from '../store/checkout';
import { getShippingOptions } from '../api/client';
import { Button } from '../components/Button';
import styles from './ShippingStep.module.css';

interface ShippingOption {
  id: string;
  name?: string;
  displayName?: string;
  description?: string;
  price: number;
  estimatedDelivery?: string | { date?: string; display?: string };
  provider: string;
  isPickup?: boolean;
  logoUrl?: string;
}

// Helper to get display string from estimatedDelivery
const getDeliveryDisplay = (delivery?: string | { date?: string; display?: string }): string => {
  if (!delivery) return 'Ukjent leveringstid';
  if (typeof delivery === 'string') return delivery;
  return delivery.display || delivery.date || 'Ukjent leveringstid';
};

const TruckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 3h15v13H1z" />
    <path d="M16 8h4l3 3v5h-7V8z" />
    <circle cx="5.5" cy="18.5" r="2.5" />
    <circle cx="18.5" cy="18.5" r="2.5" />
  </svg>
);

const HomeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9,22 9,12 15,12 15,22" />
  </svg>
);

const BoxIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <polyline points="3.27,6.96 12,12.01 20.73,6.96" />
    <line x1="12" y1="22.08" x2="12" y2="12" />
  </svg>
);

const POSTEN_LOGO_URL = 'https://res.cloudinary.com/martinsurf/image/upload/v1769983330/surfboardmerchant/nu9iwqila6m9vuoyhy1w.avif';

const PostenLogo = () => (
  <img 
    src={POSTEN_LOGO_URL} 
    alt="Posten" 
    style={{ height: '24px', width: 'auto', objectFit: 'contain' }}
  />
);

const getIcon = (option: ShippingOption) => {
  const id = (option.id || '').toLowerCase();
  const name = (option.name || option.displayName || '').toLowerCase();
  
  // Home delivery
  if (id.includes('hjem') || name.includes('hjem') || name.includes('home')) return <HomeIcon />;
  
  // Pickup point
  if (option.isPickup || id.includes('pickup') || id.includes('hent') || name.includes('butikk')) return <BoxIcon />;
  
  // Default: package/truck
  return <TruckIcon />;
};

export function ShippingStep() {
  const { 
    setStep, 
    selectShipping, 
    shippingAddress, 
    sessionId,
    customShippingOptions,
    useBuiltInShipping,
    skipShipping,
  } = useCheckoutStore();
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [options, setOptions] = useState<ShippingOption[]>([]);
  const [error, setError] = useState<string | null>(null);

  // If shipping is skipped, go directly to payment
  useEffect(() => {
    if (skipShipping) {
      // Set a dummy free shipping option
      selectShipping({
        id: 'digital',
        provider: 'none',
        method: 'digital',
        displayName: 'Digital levering',
        description: 'Ingen fysisk levering',
        price: { amount: 0, currency: 'NOK' },
        deliveryDays: 0,
        isPickup: false,
      });
      setStep('payment');
    }
  }, [skipShipping, selectShipping, setStep]);

  // Fetch shipping options (built-in or custom)
  useEffect(() => {
    if (skipShipping) return;
    
    const fetchShippingOptions = async () => {
      const postalCode = shippingAddress?.postalCode;
      
      // Use custom shipping options if provided
      if (customShippingOptions && customShippingOptions.length > 0) {
        const mappedOptions: ShippingOption[] = customShippingOptions.map((opt) => ({
          id: opt.id,
          name: opt.name,
          description: opt.description || '',
          price: opt.price.amount / 100, // Convert from øre to kroner
          estimatedDelivery: opt.estimatedDelivery,
          provider: opt.provider || 'custom',
          isPickup: opt.isPickup || false,
          logoUrl: opt.logoUrl,
        }));
        
        setOptions(mappedOptions);
        // Auto-select cheapest option
        const cheapest = mappedOptions.reduce((prev, curr) => 
          curr.price < prev.price ? curr : prev
        );
        setSelectedOption(cheapest.id);
        setIsLoading(false);
        return;
      }
      
      // Use built-in Bring/Posten integration
      if (!useBuiltInShipping) {
        // No shipping options configured - show error
        setError('Ingen fraktalternativer er konfigurert for denne butikken.');
        setIsLoading(false);
        return;
      }
      
      if (!postalCode || !sessionId) {
        setError('Mangler postnummer eller session');
        setIsLoading(false);
        return;
      }

      try {
        const response = await getShippingOptions(sessionId, postalCode);
        
        if (response.error) {
          setError(response.error.message);
          setIsLoading(false);
          return;
        }
        
        if (response.data?.options && response.data.options.length > 0) {
          // Map API response to our format
          const mappedOptions: ShippingOption[] = response.data.options.map((opt) => ({
            id: opt.id,
            name: opt.name,
            description: opt.description,
            price: opt.price.amount / 100, // Convert from øre to kroner
            estimatedDelivery: opt.estimatedDelivery,
            provider: opt.provider,
            isPickup: opt.id.toLowerCase().includes('pickup') || opt.id.toLowerCase().includes('servicepakke'),
          }));
          
          setOptions(mappedOptions);
          // Auto-select cheapest option
          const cheapest = mappedOptions.reduce((prev, curr) => 
            curr.price < prev.price ? curr : prev
          );
          setSelectedOption(cheapest.id);
        } else {
          // No shipping options returned from API
          setError('Ingen fraktalternativer tilgjengelig for dette postnummeret.');
        }
      } catch (err) {
        console.error('Failed to fetch shipping options:', err);
        setError('Kunne ikke hente fraktalternativer');
      } finally {
        setIsLoading(false);
      }
    };

    fetchShippingOptions();
  }, [sessionId, shippingAddress?.postalCode, customShippingOptions, useBuiltInShipping, skipShipping]);

  const handleContinue = () => {
    const selected = options.find(o => o.id === selectedOption);
    if (selected) {
      const deliveryDisplay = getDeliveryDisplay(selected.estimatedDelivery);
      selectShipping({
        id: selected.id,
        provider: selected.provider,
        method: selected.id,
        displayName: selected.name || selected.displayName || 'Levering',
        description: selected.description || '',
        price: { amount: selected.price * 100, currency: 'NOK' },
        deliveryDays: parseInt(deliveryDisplay) || 3,
        estimatedDelivery: { date: '', display: deliveryDisplay },
        isPickup: selected.isPickup || false,
      });
      setStep('payment');
    }
  };

  const formatPrice = (price: number) => {
    if (price === 0) return 'Gratis';
    return `${price} kr`;
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner} />
          <p>Henter fraktalternativer for {shippingAddress?.postalCode}...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <h3>Frakt ikke konfigurert</h3>
          <p>Butikken har ikke satt opp fraktalternativer enda.</p>
          <p className={styles.errorHint}>Ta kontakt med butikken for å fullføre bestillingen.</p>
          <Button variant="secondary" onClick={() => setStep('address')}>
            Gå tilbake
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title} id="shipping-heading">Velg leveringsmetode</h2>
        <p className={styles.subtitle}>Hvordan vil du motta bestillingen din?</p>
      </div>

      <div 
        className={styles.options} 
        role="radiogroup" 
        aria-labelledby="shipping-heading"
      >
        {options.map((option) => (
          <div
            key={option.id}
            role="radio"
            aria-checked={selectedOption === option.id}
            tabIndex={0}
            className={`${styles.option} ${selectedOption === option.id ? styles.optionSelected : ''}`}
            onClick={() => setSelectedOption(option.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setSelectedOption(option.id);
              }
            }}
          >
            <div className={`${styles.radio} ${selectedOption === option.id ? styles.radioSelected : ''}`} aria-hidden="true" />
            <div className={styles.optionIcon} aria-hidden="true">
              {getIcon(option)}
            </div>
            <div className={styles.optionContent}>
              <p className={styles.optionName}>{option.name || option.displayName || 'Leveringsalternativ'}</p>
              <p className={styles.optionDelivery}>{option.description || ''} • {getDeliveryDisplay(option.estimatedDelivery)}</p>
            </div>
            {/* Show custom logo if provided */}
            {option.logoUrl && (
              <div className={styles.providerLogo}>
                <img src={option.logoUrl} alt={option.provider} />
              </div>
            )}
            {/* Show Posten logo for Bring/Posten if no custom logo */}
            {!option.logoUrl && (option.provider === 'bring' || option.provider === 'posten') && (
              <div className={styles.providerLogo}>
                <PostenLogo />
              </div>
            )}
            <span className={`${styles.optionPrice} ${option.price === 0 ? styles.freeTag : ''}`} aria-label={option.price === 0 ? 'Gratis frakt' : `${option.price} kroner`}>
              {formatPrice(option.price)}
            </span>
          </div>
        ))}
      </div>

      <div className={styles.actions}>
        <Button
          variant="secondary"
          onClick={() => setStep('address')}
          className={styles.backButton}
          aria-label="Gå tilbake til adresse"
        >
          Tilbake
        </Button>
        <Button
          onClick={handleContinue}
          className={styles.continueButton}
          aria-label="Fortsett til betalingsvalg"
        >
          Fortsett til betaling

        </Button>
      </div>
    </div>
  );
}
